import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Field, getFormValues} from 'redux-form';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@material-ui/core';
import {withStyles} from '@material-ui/core/styles';

import {MergedCell, MiniHeaderCell} from '../atoms/tableCells';
import {renderTextField} from '../../../../atoms/CustomPart';

import * as validateRule from '../../../../validateRule';
import {getMaxGroupNo} from '../..';

/** 工事グループの表示数 */
const GROUP_COLUMN_COUNT = 6;

/**
 * 算定結果(CostTable)を非表示にする理由。呼び出し元(Detail/index.js)が算定ステータス・
 * データ変更有無から判定し、どちらの理由かによって表示するメッセージを切り替える。
 */
export const HIDE_COST_REASON = {
  /** 未確定(算定ステータスが算定中) */
  NOT_CONFIRMED: 'notConfirmed',
  /** 確定済だが、呼び出し元でデータが変更されたため算定結果が古い */
  DATA_CHANGED: 'dataChanged',
};

/**
 * 工事グループ(index+1)ごとの設計書番号の必須/不要バリデーション。
 * 一覧の最大工事グループ番号(maxGroupNo)以下であれば必須、それを超える場合は不要
 * (未入力であること)とする(歯抜けチェックは別途行うため、ここでは一覧に存在する
 * 個々の工事グループ番号ではなくmaxGroupNoのみで必須/不要を判定する)。
 *
 * redux-formは、Fieldのvalidate propの内容(必須かどうか)が変わっても、
 * フォーム最上位(reduxForm())のvalidateIfNeededがこのFieldの再レンダーより先に
 * 実行されてしまい、変更が1テンポ遅れて反映される(他のFieldの値を変更したタイミングで
 * ようやく反映される)ことがある。そのため、validate関数自体は常に同じ配列を渡し、
 * 関数の中でallValues(redux-formが直接渡す最新の値)から都度必須/不要かどうかを判定する。
 */
const GROUP_DOCUMENT_NO_VALIDATORS = [...Array(GROUP_COLUMN_COUNT).keys()].map(
    (index) => (value, allValues) => {
      const targetPoles = allValues?.DrSideKoziTargetPoles__r ?? [];
      const maxGroupNo = getMaxGroupNo(targetPoles);
      return targetPoles.length > 0 && (index + 1) <= maxGroupNo ?
      validateRule.required(value) : validateRule.unnecessary(value);
    });

/** ヘッダ列(左端)の幅 */
const HEADER_COLUMN_WIDTH = '100px';

/** 計列(右端)の幅 */
const TOTAL_COLUMN_WIDTH = '100px';

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  table: {
    'tableLayout': 'fixed',
    'width': '100%',
    'minWidth': 720,
    '& .MuiTableCell-root': {
      borderRight: '1px solid rgba(224, 224, 224, 1)',
      whiteSpace: 'nowrap',
    },
  },
});

export const PriceCell = withStyles((theme) => ({
  root: {
    textAlign: 'right',
    paddingRight: '4px !important',
    paddingLeft: '4px !important',
  },
}))(TableCell);

/**
 * 金額を表示用の文字列に変換する(負値は先頭に▲を付ける)。
 * @param {number} value 金額
 * @return {string} 表示用文字列
 */
const formatCurrency = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  const num = Number(value);
  return num < 0 ? `▲${Math.abs(num).toLocaleString()}` : num.toLocaleString();
};

const CostTable = (props) => {
  const {
    classes,
    values,
    hideCostReason,
    readOnly,
  } = props;

  const results = values?.DrSideKoziCostResults__r ?? [];
  const groupIndexes = [...Array(GROUP_COLUMN_COUNT).keys()];
  const targetPoles = values?.DrSideKoziTargetPoles__r ?? [];
  const maxGroupNo = getMaxGroupNo(targetPoles);

  return (
    <TableContainer component={Paper}>
      <Table size="small" className={classes.table} >
        <TableBody>
          <TableRow>
            <MiniHeaderCell width={HEADER_COLUMN_WIDTH}>工事グループ</MiniHeaderCell>
            {groupIndexes.map((index) => (
              <TableCell key={`groupNo-${index}`} align="center">{index + 1}</TableCell>
            ))}
            <TableCell width={TOTAL_COLUMN_WIDTH} align="center" rowSpan="2">計</TableCell>
          </TableRow>

          <TableRow>
            <MiniHeaderCell>設計書番号</MiniHeaderCell>
            {groupIndexes.map((index) => {
              // 工事グループ番号(index+1)が現在の最大工事グループ番号以下の場合、必須
              // (対象電柱が1件も無い場合はグループ自体が存在しないため必須にしない)
              const required =
                targetPoles.length > 0 && (index + 1) <= maxGroupNo;

              return (
                <TableCell key={`designDocumentNo-${index}`} align="right" style={{padding: '1px'}}>
                  <Field
                    name={`DrSideKoziCostResults__r[${index}]` +
                      '.DrSideKoziDocumentNo__c'}
                    className={classes.fields}
                    component={renderTextField}
                    placeholder=' 設計書番号'
                    fullWidth
                    type="text"
                    required={required}
                    disabled={readOnly}
                    inputProps={{
                      maxLength: 10,
                    }}
                    validate={[
                      GROUP_DOCUMENT_NO_VALIDATORS[index],
                      validateRule.maxLength(10),
                    ]}
                  />
                </TableCell>
              );
            })}
          </TableRow>

          {hideCostReason ? (
            <TableRow>
              <TableCell colSpan={GROUP_COLUMN_COUNT + 2}>
                {hideCostReason === HIDE_COST_REASON.DATA_CHANGED ? (
                  <>
                    <Typography variant="body1">
                        電柱情報/工事内容が変更されたため、算定結果は非表示となっています
                    </Typography>
                    <Typography variant="body2">
                        ・確定すると、変更内容が反映された算定結果が表示されます
                    </Typography>
                    <Typography variant="body2">
                        ・一度戻って再度表示すると、変更前の算定結果が表示されます
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1">
                        未確定のため、算定結果は非表示となっています
                    </Typography>
                    <Typography variant="body2">
                        ・確定すると算定結果が表示されます
                    </Typography>
                  </>
                )}
              </TableCell>
            </TableRow>
          ) : (
            <>
              <TableRow>
                <MiniHeaderCell>取付材料費</MiniHeaderCell>
                {groupIndexes.map((index) => (
                  <PriceCell key={`attachMaterial-${index}`}>
                    {formatCurrency(results[index]?.AttachMaterialCost__c)}
                  </PriceCell>
                ))}
                <PriceCell>
                  {formatCurrency(values?.AttachMaterialCost__c)}
                </PriceCell>
              </TableRow>

              <TableRow>
                <MiniHeaderCell>取付工事費</MiniHeaderCell>
                {groupIndexes.map((index) => (
                  <PriceCell key={`attachKozi-${index}`}>
                    {formatCurrency(results[index]?.AttachKoziCost__c)}
                  </PriceCell>
                ))}
                <PriceCell>
                  {formatCurrency(values?.AttachKoziCost__c)}
                </PriceCell>
              </TableRow>

              <TableRow>
                <MiniHeaderCell>撤去材料費</MiniHeaderCell>
                {groupIndexes.map((index) => (
                  <PriceCell key={`tekyoMaterial-${index}`}>
                    {formatCurrency(results[index]?.TekyoMaterialCost__c)}
                  </PriceCell>
                ))}
                <PriceCell>
                  {formatCurrency(values?.TekyoMaterialCost__c)}
                </PriceCell>
              </TableRow>

              <TableRow>
                <MiniHeaderCell>撤去工事費</MiniHeaderCell>
                {groupIndexes.map((index) => (
                  <PriceCell key={`tekyoKozi-${index}`}>
                    {formatCurrency(results[index]?.TekyoKoziCost__c)}
                  </PriceCell>
                ))}
                <PriceCell>
                  {formatCurrency(values?.TekyoKoziCost__c)}
                </PriceCell>
              </TableRow>

              <TableRow>
                <MiniHeaderCell>諸掛費</MiniHeaderCell>
                <MergedCell colSpan={GROUP_COLUMN_COUNT} align="right">
                  諸掛費率（{values?.OverheadRate__c}）
                </MergedCell>
                <PriceCell>{formatCurrency(values?.OverheadCost__c)}</PriceCell>
              </TableRow>

              <TableRow>
                <MiniHeaderCell>課税対象額</MiniHeaderCell>
                <MergedCell colSpan={GROUP_COLUMN_COUNT} align="right">
                </MergedCell>
                <PriceCell>
                  {formatCurrency(values?.TaxableAmount__c)}
                </PriceCell>
              </TableRow>

              <TableRow>
                <MiniHeaderCell>消費税</MiniHeaderCell>
                <MergedCell colSpan={GROUP_COLUMN_COUNT} align="right">
                  消費税率（{values?.ConsumptionTaxRate__c}%）
                </MergedCell>
                <PriceCell>
                  {formatCurrency(values?.ConsumptionTax__c)}
                </PriceCell>
              </TableRow>

              <TableRow>
                <MiniHeaderCell>合計金額</MiniHeaderCell>
                <MergedCell colSpan={GROUP_COLUMN_COUNT} align="right"></MergedCell>
                <PriceCell>{formatCurrency(values?.TotalAmount__c)}</PriceCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

CostTable.propTypes = {
  classes: PropTypes.object.isRequired,
  values: PropTypes.object,
  hideCostReason: PropTypes.oneOf(Object.values(HIDE_COST_REASON)),
  readOnly: PropTypes.bool,
};

const FORM_NAME = 'DrSideKoziCostDetail';

const ConnectedValues = connect((state) => ({
  values: getFormValues(FORM_NAME)(state),
}))(CostTable);

export default withStyles(styles)(ConnectedValues);
